import React, { useState, useRef, useEffect } from 'react';
import { getFilteredMonsters, hiddenByCollab } from '../../utils/monster';
import { ATTRIBUTES, ATTRIBUTE_KEYS } from '../../constants/monster';
import MonsterImage from '../../components/MonsterImage';

interface MiniMonsterSelectModalProps {
  open: boolean;
  monsterMapping: any;
  onClose: () => void;
  onSelect: (monster: any) => void;
  defaultAttr?: string;
  defaultStar?: number;
  /** 열리면 검색칸에 focus (전투기록 검색 칸에서 열 때) */
  autoFocus?: boolean;
}



const ATTR_IMG: Record<string, string> = {
  Water: `/images/water.png`,
  Fire: `/images/fire.png`,
  Wind: `/images/wind.png`,
  Light: `/images/light.png`,
  Dark: `/images/dark.png`,
};
const STAR_IMG: Record<number, string> = {
  1: `/images/star.png`,
  2: `/images/star2.png`,
};

function MonsterSelect(props: MiniMonsterSelectModalProps) {
  const [attr, setAttr] = useState(props.defaultAttr || 'Water');
  // 처음엔 5성 — 점령전에서 쓰는 몹이 거의 다 5성이라 1성으로 열면 늘 한 번 더 눌러야 했다
  const [star, setStar] = useState(props.defaultStar || 5);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // 열리면 검색칸에 바로 — 바로 글자를 쳐서 찾는다 (사용자 2026-10-27 "윈도우에서는 검색박스에 포커스")
  useEffect(() => {
    if (props.open && props.autoFocus) inputRef.current?.focus({ preventScroll: true });
  }, [props.open, props.autoFocus]);

  if (!props.open) return null;

  // 필터링 함수 공통 유틸 사용
  // 콜라보로 짝지은 몹은 몬스터 탭과 똑같이 한 칸(반반 이미지)으로만 보여준다.
  // 검색은 백엔드에서 같은 몹으로 묶이므로(utils/collab.ts) 어느 쪽을 골라도 결과는 같다.
  const filteredMonsters = getFilteredMonsters(props.monsterMapping, attr, star, search)
    .filter(([name, info]: any) => !hiddenByCollab(props.monsterMapping, name, info));
  // 차례는 서버가 매핑에 실어 준 order 를 getFilteredMonsters 가 따른다 — 여기서 정렬하지 않는다

  return (
    <div style={{
      position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh',
      background: 'rgba(30,40,60,0.18)', zIndex: 300, display: 'flex',
      alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        // 테두리 — 다크모드에서 뒤 판과 같은 남색이라 창 경계가 안 보였다(사용자 2026-10-22)
        background: '#fff', borderRadius: 14, padding: 18, minWidth: 220, border: '1.5px solid rgba(102, 126, 234, 0.45)',
        boxShadow: '0 4px 24px #1976d222', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative'
      }}>
        <button onClick={props.onClose} style={{ position: 'absolute', top: 8, right: 12, background: 'none', border: 'none', fontSize: 22, color: 'var(--text-main)', cursor: 'pointer' }}>×</button>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="이름 또는 별칭 검색"
          ref={inputRef}
          style={{
            width: 220,
            padding: '6px 12px',
            marginBottom: 12,
            border: '2px solid #1976d2',
            borderRadius: 8,
            fontSize: 15,
            outline: 'none'
          }}
        />
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          {ATTRIBUTES.map(a => (
            <button key={a.key} onClick={() => setAttr(a.key)}
              style={{
                background: attr === a.key ? '#1976d2' : 'transparent',
                border: attr === a.key ? '2px solid #1976d2' : '2px solid #e3f0ff',
                borderRadius: '50%',
                width: 38, height: 38,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: attr === a.key ? '0 2px 8px #1976d255' : 'none',
                outline: 'none',
                padding: 0
              }}
            >
              <img loading="lazy" src={ATTR_IMG[a.key]} alt={a.label} style={{ width: 26, height: 26 }} />
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          {[1,2,3,4,5].map(s => (
            <button key={s} onClick={() => setStar(s)}
              style={{
                background: star === s ? '#ffd600' : 'transparent',
                border: star === s ? '2px solid #ffd600' : '2px solid #eee',
                borderRadius: 8,
                padding: '4px 8px',
                minWidth: 0,
                minHeight: 0,
                display: 'flex', alignItems: 'center', gap: 1,
                cursor: 'pointer',
                boxShadow: star === s ? '0 2px 8px #ffd60055' : 'none',
                outline: 'none'
              }}
            >
              {Array.from({length: s}).map((_,i) => (
                <img loading="lazy" key={i} src={STAR_IMG[1]} alt="*" style={{ width: 15, height: 15 }} />
              ))}
            </button>
          ))}
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 8, justifyContent: 'center', minWidth: 512
        }}>
          {filteredMonsters.length < 120 && (
            filteredMonsters.map(([name, info, attribute, imgPath]: any) => {
              let displayName = name;
              const aliases = info[attribute]?.aliases || [];
              if (aliases.length > 0) displayName = aliases[0];
              return (
                <div key={name + attribute}
                  onClick={() => {
                    props.onSelect({ name, attribute, star, image_path: imgPath, aliases, displayName });
                  }}
                  // 그림이 칸을 꽉 채운다. 예전에는 48칸에 36 그림이라 사방이 비어 몹이 작게
                  // 보였다 — 목록에서 몹을 눈으로 찾는 화면이라 그림이 클수록 좋다
                  style={{
                    width: 56, height: 56, background: 'var(--bg-card)', borderRadius: 8, boxShadow: '0 1px 4px #0001',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                    cursor: 'pointer', border: '2px solid var(--border-main)'
                  }}>
                  <MonsterImage imgPath={imgPath} alt={name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 6, background: 'var(--bg-card)' }} />
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default MonsterSelect; 